import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { AlertCircle, CheckCircle2, RefreshCw, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import SystemStatusCards from "@/components/SystemStatusCards";

type SystemLogEntry = {
  timestamp: string;
  level: "error" | "warn";
  source: string;
  message: string;
  details?: any;
};

function SystemLogsPanel() {
  const [, setLocation] = useLocation();

  const { data: logs, isLoading: logsLoading } = trpc.system.getSystemLogs.useQuery();

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <CardTitle>System Logs</CardTitle>
          </div>
          <Button onClick={() => setLocation("/admin/logs")} variant="outline" size="sm">
            <AlertCircle className="h-4 w-4 mr-2" />
            Open System Logs
          </Button>
        </div>
        <CardDescription>
          Recent system errors and warnings
        </CardDescription>
      </CardHeader>
      <CardContent>
        {logsLoading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !logs || logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent logs</p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {logs.slice(0, 5).map((log: SystemLogEntry, index: number) => (
              <div
                key={`${log.timestamp}-${index}`}
                className="flex items-start gap-2 text-sm border-l-2 border-muted pl-3"
              >
                <Badge
                  variant={log.level === 'error' ? 'destructive' : 'secondary'}
                  className="text-xs h-5 text-xs"
                >
                  {log.level}
                </Badge>
                <div className="flex-1 space-y-0.5">
                  <span className="font-medium">{log.source}</span>
                  <p className="text-muted-foreground text-xs">{log.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(log.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            {logs.length > 5 && (
              <p className="text-xs text-muted-foreground mt-2">
                Showing {logs.length > 5 ? 5 : logs.length} most recent logs
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SystemHealth() {
  const { user, loading: authLoading } = useAuth();
  const [, navigate] = useLocation();

  const { data: healthData, isLoading: healthLoading, refetch } = trpc.system.getStatusSummary.useQuery(undefined, {
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  if (authLoading || healthLoading) {
    return (
      <div className="container max-w-6xl py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // Filter based on supervisor-safe behavior - check if user is authenticated but allow any authenticated role
  if (!user) {
    return (
      <div className="container max-w-6xl py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Authentication required. Please sign in to access system status.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/")}
            className="px-0"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">System Health</h1>
          <p className="text-muted-foreground mt-1">
            Monitor core system status and connectivity
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

          {healthData && (
            <>
              <SystemStatusCards healthData={healthData} />

              {user?.role === 'admin' && (
                <>
                  {/* System Logs */}
                  <SystemLogsPanel />
                </>
              )}
            </>
          )}
    </div>
  );
}
